import React, { useEffect, useRef } from "react";

import {
  ChartSplineIcon,
  type ChartSplineIconHandle,
} from "@/components/ui/chart-spline";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import type { BackendTradeStatistics } from "../../../../types/trade";
import { STATISTICS_NO_DATA_TIMEFRAME_MESSAGE } from "./statisticsChartTooltip";

export type StatisticsEmptyStateVariant = "loading" | "no-data";

export interface StatisticsEmptyStateProps {
  variant: StatisticsEmptyStateVariant;
  /** Parent flex column already sizes the shell — only center the card */
  fillParent?: boolean;
  className?: string;
  emptyClassName?: string;
  /** Override default title (e.g. Reports list: "Loading reports"). */
  title?: string;
  /** Override default description. */
  description?: string;
  /** Optional CTA rendered directly under the description. */
  action?: React.ReactNode;
}

/** Min height for the Statistics page column (below inset header + shell padding). */
export function statisticsPageMinHeight(shadcnInset: boolean): string {
  return shadcnInset ? "calc(100svh - 7rem)" : "calc(100vh - 12rem)";
}

export function StatisticsEmptyState({
  variant,
  fillParent = false,
  className,
  emptyClassName,
  title,
  description,
  action,
}: StatisticsEmptyStateProps) {
  const isLoading = variant === "loading";
  const chartIconRef = useRef<ChartSplineIconHandle>(null);
  const defaultDescription = isLoading
    ? "Please wait while we load your trading statistics."
    : STATISTICS_NO_DATA_TIMEFRAME_MESSAGE;
  const resolvedDescription =
    description !== undefined ? description : defaultDescription;

  useEffect(() => {
    if (isLoading) return;
    void chartIconRef.current?.startAnimation();
  }, [isLoading]);

  return (
    <div
      data-statistics-empty-shell=""
      className={cn(
        "flex w-full flex-col items-center justify-center px-6",
        fillParent && "min-h-0 flex-1",
        className
      )}
    >
      <Empty
        data-statistics-empty=""
        className={cn(
          "w-full max-w-lg shrink-0 min-h-[280px] border-0 bg-transparent py-10 shadow-none",
          emptyClassName
        )}
      >
        <EmptyHeader>
          <EmptyMedia variant="icon">
            {isLoading ? (
              <Spinner
                className="size-4 text-[color:var(--brand-tropical-surf)]"
                aria-hidden
              />
          ) : (
            <ChartSplineIcon
              ref={chartIconRef}
              size={16}
              className="cursor-default text-gray-500 transition-colors hover:text-[color:var(--brand-tropical-surf)] dark:text-white"
              aria-hidden
            />
          )}
          </EmptyMedia>
          <EmptyTitle className="font-[family-name:var(--font-primary)] text-base font-normal text-[color:var(--brand-tropical-surf)]">
            {title ??
              (isLoading ? "Loading statistics" : "No statistics available")}
          </EmptyTitle>
          {resolvedDescription ? (
            <EmptyDescription className="font-[family-name:var(--font-primary)] font-normal text-gray-500 dark:text-white">
              {resolvedDescription}
            </EmptyDescription>
          ) : null}
        </EmptyHeader>
        {action ? <EmptyContent>{action}</EmptyContent> : null}
      </Empty>
    </div>
  );
}

/** Centered empty state for a statistics tab panel (full tab or comparison period). */
export function StatisticsTabEmptyState({
  compact = false,
  className,
}: {
  compact?: boolean;
  className?: string;
}) {
  if (compact) {
    return (
      <div
        className={cn(
          "flex w-full min-h-[280px] flex-col items-center justify-center px-6",
          className
        )}
      >
        <StatisticsEmptyState variant="no-data" />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex min-h-0 w-full flex-1 flex-col items-center justify-center",
        className
      )}
    >
      <StatisticsEmptyState variant="no-data" fillParent />
    </div>
  );
}

/** True when the period has at least one trade in backend statistics. */
export function tradeStatisticsHasData(
  stats: BackendTradeStatistics | null | undefined
): boolean {
  if (!stats) return false;
  return (stats.closed_trades ?? 0) > 0 || (stats.total_trades ?? 0) > 0;
}

/** Tab hooks start with `data: null`; treat that as loading until fetch resolves or errors. */
export function isStatisticsFetchPending(
  data: unknown,
  error?: string | null
): boolean {
  return data == null && !error;
}

/** Full-tab loading placeholder while tab-scoped statistics fetch on first mount. */
export function StatisticsTabLoadingState({
  compact = false,
  className,
}: {
  compact?: boolean;
  className?: string;
}) {
  if (compact) {
    return (
      <div
        className={cn(
          "flex w-full min-h-[280px] flex-col items-center justify-center px-6",
          className
        )}
      >
        <StatisticsEmptyState variant="loading" />
      </div>
    );
  }

  return (
    <StatisticsEmptyState variant="loading" fillParent className={className} />
  );
}
